/**
 * Les sons de l'app.
 *
 * Un son a un nom, et il est joué par un petit synthétiseur (Web Audio) : aucun
 * fichier à charger, aucune licence. Essayés comme sons provisoires, ils ont
 * été gardés tels quels.
 *
 * Deux contraintes de WebKit :
 * - l'audio ne démarre que dans un geste de l'utilisateur. `primeSound()` se
 *   pose donc sur le toucher qui précède le son (le bouton « Acheter »), même
 *   si le son part une seconde plus tard ;
 * - `navigator.audioSession.type = 'ambient'` fait respecter le mode
 *   silencieux et ne coupe pas la musique déjà en cours.
 */

export type SoundName = 'cardReveal' | 'cardRevealRare' | 'coin' | 'streak' | 'cheer' | 'fanfare' | 'pop'

let ctx: AudioContext | null = null

// ── Le réglage ────────────────────────────────────────────────────────────
// Un réglage de l'APPAREIL, pas du compte : la tablette du salon et le
// téléphone du parent n'ont pas les mêmes oreilles autour d'eux. Allumé par
// défaut ; le mode silencieux du téléphone coupe tout de toute façon (session
// audio 'ambient').
const SOUND_KEY = 'harmonia_sound'

export function isSoundEnabled(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) !== 'off'
  } catch {
    return true
  }
}

export function setSoundEnabled(on: boolean): void {
  try {
    localStorage.setItem(SOUND_KEY, on ? 'on' : 'off')
  } catch {
    // Stockage indisponible : le réglage vaut pour la session seulement.
  }
}

function context(): AudioContext | null {
  if (typeof window === 'undefined') return null
  if (ctx) return ctx
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  try {
    const session = (navigator as unknown as { audioSession?: { type: string } }).audioSession
    if (session) session.type = 'ambient'
    ctx = new Ctor()
  } catch {
    ctx = null
  }
  return ctx
}

/** À appeler dans le geste qui précède un son différé. */
export function primeSound(): void {
  const c = context()
  if (c?.state === 'suspended') void c.resume().catch(() => {})
}

/** Une note de clochette : fondamentale + harmonique, attaque sèche, longue queue. */
function bell(c: AudioContext, out: AudioNode, freq: number, at: number, gain: number, decay = 0.7) {
  for (const [ratio, level, type] of [[1, 1, 'sine'], [2.01, 0.28, 'sine'], [3, 0.08, 'triangle']] as const) {
    const osc = c.createOscillator()
    const env = c.createGain()
    osc.type = type
    osc.frequency.value = freq * ratio
    env.gain.setValueAtTime(0.0001, at)
    env.gain.exponentialRampToValueAtTime(gain * level, at + 0.006)
    env.gain.exponentialRampToValueAtTime(0.0001, at + decay / ratio)
    osc.connect(env).connect(out)
    osc.start(at)
    osc.stop(at + decay + 0.05)
  }
}

/** Do-mi-sol-do en montée, puis un scintillement aigu qui s'éteint. */
function synthReveal(c: AudioContext, rare: boolean) {
  const out = c.createGain()
  out.gain.value = 0.22
  out.connect(c.destination)
  const t = c.currentTime + 0.02
  const notes = rare ? [1046.5, 1318.5, 1568, 2093, 2637] : [1046.5, 1318.5, 1568, 2093]
  notes.forEach((f, i) => bell(c, out, f, t + i * 0.07, i === notes.length - 1 ? 0.9 : 0.6, i === notes.length - 1 ? 1.1 : 0.5))
  const sparkle = rare ? [3136, 3951, 3520, 4186, 3729, 4699] : [3136, 3951, 3520, 4186]
  const from = t + notes.length * 0.07 + 0.04
  sparkle.forEach((f, i) => bell(c, out, f, from + i * 0.06, 0.12 * (1 - i / sparkle.length), 0.35))
}

/** La pièce qui tombe dans le compteur : deux notes sèches, la seconde plus haute. */
function synthCoin(c: AudioContext) {
  const out = c.createGain()
  out.gain.value = 0.18
  out.connect(c.destination)
  const t = c.currentTime + 0.01
  bell(c, out, 987.77, t, 0.5, 0.12)
  bell(c, out, 1318.5, t + 0.075, 0.7, 0.45)
}

/** Un palier de série : sol-do-mi-sol, plus vif que la révélation d'une carte. */
function synthStreak(c: AudioContext) {
  const out = c.createGain()
  out.gain.value = 0.2
  out.connect(c.destination)
  const t = c.currentTime + 0.02
  const notes = [783.99, 1046.5, 1318.5, 1568]
  notes.forEach((f, i) => bell(c, out, f, t + i * 0.06, i === notes.length - 1 ? 0.85 : 0.5, i === notes.length - 1 ? 0.9 : 0.35))
}

/** Un bravo reçu : deux notes douces qui montent, comme une sonnette. */
function synthCheer(c: AudioContext) {
  const out = c.createGain()
  out.gain.value = 0.2
  out.connect(c.destination)
  const t = c.currentTime + 0.02
  bell(c, out, 1318.5, t, 0.6, 0.6)
  bell(c, out, 1760, t + 0.16, 0.7, 1)
}

/** La journée bouclée : un accord qui monte en deux temps, puis qui sonne. */
function synthFanfare(c: AudioContext) {
  const out = c.createGain()
  out.gain.value = 0.2
  out.connect(c.destination)
  const t = c.currentTime + 0.02
  ;[523.25, 659.25, 783.99].forEach((f, i) => bell(c, out, f, t + i * 0.09, 0.5, 0.3))
  ;[1046.5, 1318.5, 1568].forEach(f => bell(c, out, f, t + 0.34, 0.45, 1.3))
}

/** Un pion posé : une note brève et ronde. */
function synthPop(c: AudioContext) {
  const out = c.createGain()
  out.gain.value = 0.16
  out.connect(c.destination)
  bell(c, out, 880, c.currentTime + 0.005, 0.6, 0.14)
}

const SYNTH: Record<SoundName, (c: AudioContext) => void> = {
  cardReveal: c => synthReveal(c, false),
  cardRevealRare: c => synthReveal(c, true),
  coin: synthCoin,
  streak: synthStreak,
  cheer: synthCheer,
  fanfare: synthFanfare,
  pop: synthPop,
}

/** Joue un son. Ne lève jamais : un son manqué ne doit rien casser. */
export async function playSound(name: SoundName): Promise<void> {
  if (!isSoundEnabled()) return
  const c = context()
  if (!c) return
  try {
    if (c.state === 'suspended') await c.resume()
    SYNTH[name](c)
  } catch {
    // Pas de son, pas de drame.
  }
}
